import { EventEmitter, Inject, Injectable } from "@angular/core";
import { DefaultLanguageChangeEvent, LanguageChangeEvent, LocalizationChangeEvent, MissingLocalizationHandlerParams } from "./localizaton.model";
import { LocalizationHub } from "./localization.hub";
import { Observable, Subject, concat, concatMap, defer, forkJoin, isObservable, map, of, shareReplay, switchMap, take } from "rxjs";
import { isDefined, mergeDeep } from "../helpers/helper";
import { LocalizationParser } from "./localization.parser";
import { LocalizationLoader } from "./localization.loader";
import { MissingLocalizationHandler } from "./missing-localization-handler";
declare interface Window {
    navigator: any;
}
declare const window: Window;

@Injectable({
    providedIn: 'root'
})
export class LocalizationService {
    getLoginUserCountryId(): string {
      throw new Error('Method not implemented.');
    }
    private loadingLocalizations!: Observable<any>;
    private pending: boolean = false;
    private _localizationRequests: any = {};
    private useDefaultLanguage: boolean = true;
    private extend: boolean = false;

    //defaultLanguage: string = 'en-US';
    constructor(public hub: LocalizationHub,
        public localizationLoader: LocalizationLoader,
        public parser: LocalizationParser,
        public missingLocalizationHandler: MissingLocalizationHandler,
    ) {
        /** set the default language from configuration */
        //if (defaultLanguage) {
        //this.setDefaultLanguage('USA_eng');
        //}
    }
    public setDefaultLanguage(language: string, screenParts: string[]): void {
        if (language === this.hub.defaultLanguage) {
            return;
        }

        let pending = this.retrieveLocalizations(language, screenParts);

        if (typeof pending !== "undefined") {
            // on init set the defaultLang immediately
            if (this.hub.defaultLanguage == null) {
                this.hub.defaultLanguage = language;
            }

            pending.pipe(take(1))
                .subscribe((res: any) => {
                    this.changeDefaultLanguage(language);
                });
        } else { // we already have this language
            this.changeDefaultLanguage(language);
        }
    }

    public getDefaultLanguage(): string {
        return this.hub.defaultLanguage;
    }
    public setLocalizationScreen(language: string, screen: string) {
        this.use(language, [screen]);
    }
    public use(language: string, screenParts: string[]): Observable<any> {

        // don't change the language if the language given is already selected
        if (language === this.hub.currentLanguage && this.isLocalizationAvailable(language, screenParts)) {
            return of(this.hub.localizations[language]);
        }

        let pending = this.retrieveLocalizations(language, screenParts);

        if (typeof pending !== "undefined") {
            // on init set the currentLang immediately
            if (!this.hub.currentLanguage) {
                this.hub.currentLanguage = language;
            }

            pending.pipe(take(1))
                .subscribe((res: any) => {
                    this.changeLanguage(language);
                });

            return pending;
        } else { // we have this language, return an Observable
            this.changeLanguage(language);

            return of(this.hub.localizations[language]);
        }
    }

    private retrieveLocalizations(language: string, screenParts: string[]): Observable<any> | undefined {
        let pending: Observable<any> | undefined;

        // if this language is unavailable or extend is true, ask for it
        let newLocalizationsRequired = this.isLocalizationAvailable(language, screenParts);
        if ((!newLocalizationsRequired) || this.extend) {
            if (!newLocalizationsRequired)
                this._localizationRequests[language] = this.getLocalization(language, screenParts);
            else
                this._localizationRequests[language] = this._localizationRequests[language] || this.getLocalization(language, screenParts);
            pending = this._localizationRequests[language];
        }

        return pending;
    }
    private isLocalizationAvailable(language: string, screenParts: string[]): boolean {
        if (typeof this.hub.localizations[language] === "undefined")
            return false;

        let parts = Object.keys(this.hub.localizations[language]);
        return !screenParts.some(r => parts.indexOf(r) === -1);
    }
    public getLocalization(language: string, screenParts: string[]): Observable<any> {
        this.pending = true;
        //this.extend = true;
        //let localizeobj: any = { AE_eng: ['about', 'activityLogs'], AT_eng: ['about'], AU_eng: ['about'] };
        //let currentLangScreens = localizeobj[language];
        if (screenParts) {
            screenParts.forEach((element: any) => {
                const loadingLocalizations = this.localizationLoader.getLocalization(language + '_' + element).pipe(
                    shareReplay(1),
                    take(1),
                );

                this.loadingLocalizations = loadingLocalizations;

                this.loadingLocalizations
                    .subscribe({
                        next: (res: Object) => {
                            if (this.hub.localizations[language]) {
                                this.hub.localizations[language][element] = res;
                            } else {
                                this.hub.localizations[language] = { [element]: res };
                            }
                            //this.localizations[language] = this.extend && this.localizations[language] ? { [element]: { ...res, ...this.localizations[language] } } : { [element]: res };
                            this.updateLanguages();
                            this.pending = false;
                        },
                        error: (err: any) => {
                            this.pending = false;
                        }
                    });

                return loadingLocalizations;
            });
        }
        return new Observable();
    }

    public setLocalization(language: string, localizations: Object, shouldMerge: boolean = false): void {
        if ((shouldMerge || this.extend) && this.hub.localizations[language]) {
            this.hub.localizations[language] = mergeDeep(this.hub.localizations[language], localizations);
        } else {
            this.hub.localizations[language] = localizations;
        }
        this.updateLanguages();
        this.hub.onLocalizationChange.emit({ language: language, localizations: this.hub.localizations[language] });
    }

    public getLanguages(): Array<string> {
        return this.hub.languages;
    }

    public addLanguages(languages: Array<string>): void {
        languages.forEach((language: string) => {
            if (this.hub.languages.indexOf(language) === -1) {
                this.hub.languages.push(language);
            }
        });
    }

    private updateLanguages(): void {
        this.addLanguages(Object.keys(this.hub.localizations));
    }

    public getParsedResult(localizations: any, key: any, interpolateParams?: Object): any {
        let res: string | Observable<string> | undefined;

        if (key instanceof Array) {
            let result: any = {},
                observables: boolean = false;
            for (let k of key) {
                result[k] = this.getParsedResult(localizations, k, interpolateParams);
                if (isObservable(result[k])) {
                    observables = true;
                }
            }
            if (observables) {
                const sources = key.map(k => isObservable(result[k]) ? result[k] : of(result[k] as string));
                return forkJoin(sources).pipe(
                    map((arr: Array<string>) => {
                        let obj: any = {};
                        arr.forEach((value: string, index: number) => {
                            obj[key[index]] = value;
                        });
                        return obj;
                    })
                );
            }
            return result;
        }

        if (localizations) {
            res = this.parser.interpolate(this.parser.getValue(localizations, key), interpolateParams);
        }

        if (typeof res === "undefined" && this.hub.defaultLanguage != null && this.hub.defaultLanguage !== this.hub.currentLanguage && this.useDefaultLanguage) {
            res = this.parser.interpolate(this.parser.getValue(this.hub.localizations[this.hub.defaultLanguage], key), interpolateParams);
        }

        if (typeof res === "undefined") {
            let params: MissingLocalizationHandlerParams = { key, localizationService: this };
            if (typeof interpolateParams !== 'undefined') {
                params.interpolateParams = interpolateParams;
            }
            res = this.missingLocalizationHandler.handle(params);
        }

        return typeof res !== "undefined" ? res : key;
    }

    public get(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
        if (!isDefined(key) || !key.length) {
            throw new Error(`Parameter "key" required`);
        }
        // check if we are loading a new localization to use
        if (this.pending) {

            return this.loadingLocalizations.pipe(
                concatMap((res: any) => {
                    res = this.getParsedResult(res, key, interpolateParams);
                    return isObservable(res) ? res : of(res);
                }),
            );
        } else {
            let res = this.getParsedResult(this.hub.localizations[this.hub.currentLanguage], key, interpolateParams);
            return isObservable(res) ? res : of(res);
        }
    }

    public getStreamOnLocalizationChange(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
        if (!isDefined(key) || !key.length) {
            throw new Error(`Parameter "key" required`);
        }

        return concat(
            defer(() => this.get(key, interpolateParams)),
            this.hub.onLocalizationChange.pipe(
                switchMap((event: LocalizationChangeEvent) => {
                    const res = this.getParsedResult(event.localizations, key, interpolateParams);
                    if (typeof res.subscribe === 'function') {
                        return res;
                    } else {
                        return of(res);
                    }
                })
            )
        );
    }

    public stream(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
        if (!isDefined(key) || !key.length) {
            throw new Error(`Parameter "key" required`);
        }

        return concat(
            defer(() => this.get(key, interpolateParams)),
            this.hub.onLanguageChange.pipe(
                switchMap((event: LanguageChangeEvent) => {
                    const res = this.getParsedResult(event.localizations, key, interpolateParams);
                    return isObservable(res) ? res : of(res);
                })
            ));
    }

    public instant(key: string | Array<string>, interpolateParams?: Object): string | any {
        if (!isDefined(key) || !key.length) {
            throw new Error(`Parameter "key" required`);
        }

        let res = this.getParsedResult(this.hub.localizations[this.hub.currentLanguage], key, interpolateParams);
        if (isObservable(res)) {
            if (key instanceof Array) {
                let obj: any = {};
                key.forEach((value: string, index: number) => {
                    obj[key[index]] = key[index];
                });
                return obj;
            }
            return key;
        } else {
            return res;
        }
    }

    public set(key: string, value: string, screenPart: string, language: string = this.hub.currentLanguage): void {
        this.hub.localizations[language][screenPart][key] = value;
        this.updateLanguages();
        this.hub.onLocalizationChange.emit({ language: language, localizations: this.hub.localizations[language] });
    }

    private changeLanguage(language: string): void {
        this.hub.currentLanguage = language;
        this.hub.onLanguageChange.emit({ language: language, localizations: this.hub.localizations[language] });

        // if there is no default language, use the one that we just set
        if (this.hub.defaultLanguage == null) {
            this.changeDefaultLanguage(language);
        }
    }

    private changeDefaultLanguage(language: string): void {
        this.hub.defaultLanguage = language;
        this.hub.onDefaultLanguageChange.emit({ language: language, localizations: this.hub.localizations[language] });
    }

    public reloadLanguage(language: string, screenParts: string[]): Observable<any> {
        this.resetLanguage(language);
        return this.getLocalization(language, screenParts);
    }

    public resetLanguage(language: string): void {
        this._localizationRequests[language] = undefined;
        this.hub.localizations[language] = undefined;
    }

    public getBrowserLanguage(): string | undefined {
        if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
            return undefined;
        }

        let browserLanguage: any = window.navigator.languages ? window.navigator.languages[0] : null;
        browserLanguage = browserLanguage || window.navigator.language || window.navigator.browserLanguage || window.navigator.userLanguage;

        if (typeof browserLanguage === 'undefined') {
            return undefined
        }

        if (browserLanguage.indexOf('-') !== -1) {
            browserLanguage = browserLanguage.split('-')[0];
        }

        if (browserLanguage.indexOf('_') !== -1) {
            browserLanguage = browserLanguage.split('_')[0];
        }

        return browserLanguage;
    }

    public getBrowserCultureLanguage(): string | undefined {
        if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
            return undefined;
        }

        let browserCultureLang: any = window.navigator.languages ? window.navigator.languages[0] : null;
        browserCultureLang = browserCultureLang || window.navigator.language || window.navigator.browserLanguage || window.navigator.userLanguage;

        return browserCultureLang;
    }
}
